import React, { useContext } from "react"
import tw from "twin.macro"
import { ContactContext } from "../../../../contexts/Contact"
import {
  setActiveView,
  setPreviousView,
} from "../../../../contexts/Contact/actions"
import { LanguageContext } from "../../../../contexts/Language"
import { reformattedContactView } from "../../../../helpers"
import useTealiumEvent from "../../../../hooks/Tealium/useTealiumEvent"
import { useTealiumContext } from "../../../../contexts/Tealium"
import { FormHeadingProps } from "./FormPage.d"

const FormHeading: React.FC<FormHeadingProps> = ({
  showFormHeading = true,
  formHeading,
  ...remainingProps
}) => {
  const [{ dealer, activeView, vehicle }, dispatch] = useContext(ContactContext)
  const { _ } = useContext(LanguageContext)
  
  // Tealium
  const { trackTealEvent } = useTealiumEvent()
  const { contactDealerData } = useTealiumContext()

  // Helper function to determine vehicle availability
  const getVehicleAvailability = () => {
    // First check if we have vehicle data with VIN
    if (vehicle?.vin && vehicle?.available) {
      return vehicle.available.toLowerCase() === "now" ? "Available Now" : "Available Soon"
    }
    
    // Then check contact dealer data from inventory
    const inventoryAvailability = contactDealerData?.fromInventory?.vehicle_avail
    if (inventoryAvailability) {
      if (Array.isArray(inventoryAvailability)) {
        // For inventory results with multiple vehicles, return the formatted array
        return inventoryAvailability.map(avail => 
          avail.toLowerCase() === "now" ? "Available Now" : "Available Soon"
        )
      } else {
        // Handle single availability value
        return inventoryAvailability.toLowerCase() === "now" ? "Available Now" : "Available Soon"
      }
    }
    
    // Check fromSelection as well
    const selectionAvailability = contactDealerData?.fromSelection?.vehicle_avail
    if (selectionAvailability) {
      if (Array.isArray(selectionAvailability)) {
        return selectionAvailability.map(avail => 
          avail.toLowerCase() === "now" ? "Available Now" : "Available Soon"
        )
      } else {
        return selectionAvailability.toLowerCase() === "now" ? "Available Now" : "Available Soon"
      }
    }
    
    // Return undefined if no vehicle availability data exists
    return undefined
  }

  const handleClick = e => {
    e.preventDefault()
    const eventData: any = {
      tealium_event: "contact_form_nav",
      contact_question: reformattedContactView(activeView),
      dropdown_selected: formHeading ? "Change:zip" : "Change:dealer",
    }

    // Add vehicle_avail if available
    const vehicleAvailability = getVehicleAvailability()
    if (vehicleAvailability) {
      eventData.vehicle_avail = vehicleAvailability
    }

    trackTealEvent(eventData)
    
    if (activeView != "SelectDealerView") {
      dispatch(setPreviousView(activeView))
    }
    if (activeView === "SelectDealerView") {
      dispatch(setActiveView("DealerLocationView"))
    } else {
      dispatch(setActiveView("ConfirmZip"))
    }
  }
  
  return (
    <div css={tw`lg:(h-auto)`} {...remainingProps}>
      {showFormHeading && (
        <>
          <p
            css={[
              tw`text-2xl font-semibold normal-case pr-28 leading-6 lg:pr-0`,
            ]}
          >
            {formHeading || dealer?.Name}
          </p>
          <button
            css={tw`text-red-400 text-xs py-1.5 z-10 relative inline-block`}
            onClick={e => handleClick(e)}
            analytics-id={`Change:contact form:${reformattedContactView(
              activeView,
            )}`}
          >
            {_("Change")}
          </button>
        </>
      )}
    </div>
  )
}

export default FormHeading
